import { consoleLoggerFactory, getDefaultLoggerOptions, type ILogger, LogLevels } from "@/types/console-logger";
import { DateTime } from "luxon";
import { goFetch, isSuccessState } from "@/utils/network-utils";
import type { VersionQueryPayload } from "@tmi/types/api/fetch";

const logger: ILogger = consoleLoggerFactory.create( getDefaultLoggerOptions( LogLevels.ERROR ) );

type VersionInfo = {
    breaking: number,
    feature: number,
    fix: number
}

export function useVersionInfo() {
    const currentVersion: string = __APP_VERSION__;

    return {
        currentVersion
    };
}

function isNewerVersion( latestVs: string ): boolean {
    const latestVersion: VersionInfo = parseVersion( latestVs );
    const { currentVersion } = useVersionInfo();
    const clientVersion: VersionInfo = parseVersion( currentVersion );

    if ( latestVersion.breaking <= clientVersion.breaking ) {
        if ( latestVersion.feature <= clientVersion.feature ) {
            if ( latestVersion.fix <= clientVersion.fix ) {
                logger.debug( 'Newer version IS NOT available.' );
                return false;
            }
        }
    }

    logger.error( 'Newer version IS available.' );
    return true;
}

function parseVersion( vs: string ): VersionInfo {
    const tokens: string[] = vs.split( '.' );
    return {
        breaking: parseInt( tokens[ 0 ] ),
        feature: parseInt( tokens[ 1 ] ),
        fix: parseInt( tokens[ 2 ] )
    }
}

function isExpired( ttlInMilliseconds: string ) {
    const ttlDate: DateTime = DateTime.fromMillis( parseInt( ttlInMilliseconds ) );
    logger.debug( `ttlDate expires @ ${ ttlDate.toString() }` )
    const currentDate: DateTime = DateTime.now();
    return ( !ttlDate.isValid || ttlDate < currentDate );
}

async function getlatestAvailableVersion() : Promise<string | undefined>
{
    try
    {
        const response =
            await goFetch<string, VersionQueryPayload>( {
                endpoint: 'ux/app/getversion',
                payload: { appId: 'bc8a907b-0e60-49a2-96ba-43a9b227c1d0' }
            } );

        return response.data;
    }
    catch
    {
        return;
    }
}

export async function isNewVersionAvailable(): Promise<boolean> {
    const checkVersionTiming: string | null | undefined = localStorage.getItem( 'client-version' );
    if ( null == checkVersionTiming || isExpired( checkVersionTiming ) ) {
        logger.debug( 'checking latest version available' );
        localStorage.setItem( 'client-version', JSON.stringify( DateTime.now().plus( { minutes: 5 } ).toMillis() ) );

        const latestVersion = await getlatestAvailableVersion()

        if ( latestVersion ) {
            logger.debug( `latestVersion: ${ latestVersion }` );

            if ( isNewerVersion( latestVersion ) ) {
                return true;
            }
        }
    }

    return false;
}
